import { Card, CardContent } from '../../ui';
import { STANCE_META } from '../../../constants/sentiment';
import { cn } from '../../../utils/cn';

/**
 * The first thing on a pulse: are they with you, or against you?
 *
 * Emotion says what people feel. It cannot say whether they are on your side.
 * On one run 156 angry replies read as "negative" when many were allies
 * attacking a third party - so the page led with a feeling and buried the
 * only question a manager actually has. This strip answers it first, and
 * "Against you" is deliberately the loudest cell: those are the replies to
 * read.
 *
 * Counts come from run.totals (snapshotted when the run finished); if a run
 * predates stance and has not been relabelled, the grid rows are summed as a
 * fallback, and if neither has stance the strip says so rather than showing
 * zeros as if they were measured.
 */
function countsFrom(totals, grid) {
	if (totals && (totals.for || totals.against || totals.noStance || totals.unclear)) {
		return { for: totals.for || 0, against: totals.against || 0, none: totals.noStance || 0 };
	}
	const sum = { for: 0, against: 0, none: 0 };
	for (const r of grid || []) {
		if (!r.stance) continue;
		sum.for += r.stance.for || 0;
		sum.against += r.stance.against || 0;
		sum.none += r.stance.none || 0;
	}
	return sum;
}

export function StanceStrip({ totals, grid, onOpen, onContext, accountHandle, className }) {
	const c = countsFrom(totals, grid);
	const total = c.for + c.against + c.none;
	const unclear = totals?.unclear || 0;
	const accountName = accountHandle ? `@${accountHandle}` : 'this account';

	if (!total && !unclear) {
		return (
			<Card className={className}>
				<CardContent className="p-5 text-sm text-muted-foreground">
					This pulse hasn't been read for stance yet. It will show support and opposition toward {accountName} once it has.
				</CardContent>
			</Card>
		);
	}

	const pct = (n) => total ? Math.round((n / total) * 100) : 0;
	const cells = [
		{ key: 'against', n: c.against, meta: STANCE_META.against, lead: true, hint: 'The replies to read first' },
		{ key: 'for', n: c.for, meta: STANCE_META.for, hint: `Replies supporting or defending ${accountName}` },
		{ key: 'none', n: c.none, meta: STANCE_META.none, hint: 'Questions, tags, links, or feelings about the situation itself' },
	];

	return (
		<Card className={className}>
			<CardContent className="p-0">
				<div className="flex items-center justify-between px-5 pt-4">
					<div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Where the room stands</div>
					{/* `total` is every labelled reply, including the ones with NO
					    position - the bar and the third cell both show them. Calling
					    them "replies with a position" contradicted the "no position"
					    count printed directly beneath it. */}
					<div className="text-xs text-muted-foreground">{total} resolved replies</div>
				</div>
				<div className="mt-2 flex h-2 overflow-hidden" title={`${c.against} against · ${c.for} with · ${c.none} no position`}>
					{c.against > 0 && <span className={STANCE_META.against.bg} style={{ width: `${pct(c.against)}%` }} />}
					{c.for > 0 && <span className={STANCE_META.for.bg} style={{ width: `${pct(c.for)}%` }} />}
					{c.none > 0 && <span className={STANCE_META.none.bg} style={{ width: `${pct(c.none)}%` }} />}
				</div>
				<div className="grid divide-y divide-border sm:grid-cols-3 sm:divide-x sm:divide-y-0">
					{cells.map(({ key, n, meta, lead, hint }) => (
						<button
							key={key}
							type="button"
							disabled={n === 0}
							onClick={() => n > 0 && onOpen?.({ stance: key, title: meta.label, text: hint })}
							aria-label={n > 0 ? `${meta.label}: ${n} replies, ${pct(n)}%. View these replies` : `${meta.label}: no replies this pulse`}
							className={cn(
								'group flex flex-col items-start gap-0.5 px-5 py-4 text-left transition-colors',
								'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-inset',
								n > 0 ? 'hover:bg-muted' : 'cursor-default',
								lead && n > 0 && 'bg-stance-against/10 hover:bg-stance-against/15'
							)}
						>
							<span className={cn('text-xs font-semibold uppercase tracking-wide', n > 0 ? meta.text : 'text-muted-foreground')}>{meta.label}</span>
							<span className={cn('tabular-nums', lead ? 'text-3xl font-semibold' : 'text-2xl font-semibold', n > 0 ? 'text-foreground' : 'text-muted-foreground')}>
								{n}
								<span className="ml-1.5 text-sm font-normal text-muted-foreground">{pct(n)}%</span>
							</span>
							<span className="text-xs text-muted-foreground">{n > 0 ? hint : 'None this pulse'}</span>
							{/* The whole cell is a button, but nothing said so. This is the cue. */}
							{n > 0 && <span className="mt-1 text-xs font-medium text-brand-600 underline decoration-brand-300 underline-offset-2 group-hover:no-underline">View details</span>}
						</button>
					))}
				</div>
				{unclear > 0 && <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-5 py-3 text-sm"><span>{unclear} unclear</span><span className="text-xs text-muted-foreground">Excluded from these percentages</span>{onContext && <button type="button" onClick={onContext} className="font-medium text-brand-600 hover:underline">Review context</button>}</div>}
			</CardContent>
		</Card>
	);
}
